import { ANDROID_WRAPPER_EVENTS, LOBBY_EVENTS } from '@/event-manager/types';
import { useStore } from '@/store/useStore';
import { sendAndroidWrapperEvent } from '@/utilities/helpers/wrapperHelpers';
import {
  isDetachedMode,
  isEmbeddedIframe,
  isEmbeddedInLobby,
  isEmbeddedQueryRequested,
} from './embedded-mode';

export const EMBEDDED_AVAILABILITY = {
  CLOSE_MESSAGE_TYPE: 'GAME_LAUNCHER_CLOSE',
  POPUP_FEATURES: 'width=1400,height=900',
  POPUP_TARGET: '_blank',
} as const;

export type EmbeddedBootstrapResult =
  | 'standalone'
  | 'embedded'
  | 'fallback-popup';

export const notifyLobbyEmbeddedClose = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (window.parent !== window) {
      window.parent.postMessage(
        { type: EMBEDDED_AVAILABILITY.CLOSE_MESSAGE_TYPE },
        window.location.origin,
      );
    }
  } catch {
    // Parent access can fail when the shell is not same-origin.
  }
};

/**
 * The lobby stays mounted under the game window (embed iframe on web, background
 * webview in the native app), so its Recently Played zone only refetches when told to.
 */
export const notifyLobbyRefreshRecentlyPlayed = (): void => {
  if (useStore.getState().runtime.wrapperType.isMobile) {
    sendAndroidWrapperEvent(ANDROID_WRAPPER_EVENTS.REFRESH_RECENTLY_PLAYED, '');
    return;
  }

  if (!isEmbeddedInLobby()) {
    return;
  }

  try {
    window.parent.postMessage(
      { type: LOBBY_EVENTS.REFRESH_RECENTLY_PLAYED },
      window.location.origin,
    );
  } catch {
    // Parent access can fail when the shell is not same-origin.
  }
};

/** Opens a standalone game-window popup and closes the unusable embed shell. */
export const fallbackToPopupLaunch = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const url = new URL(window.location.href);
  url.searchParams.delete('embedded');

  const popup = window.open(
    url.toString(),
    EMBEDDED_AVAILABILITY.POPUP_TARGET,
    EMBEDDED_AVAILABILITY.POPUP_FEATURES,
  );
  notifyLobbyEmbeddedClose();

  return Boolean(popup);
};

export const bootstrapEmbeddedOrFallbackToPopup =
  (): EmbeddedBootstrapResult => {
    if (!isEmbeddedQueryRequested() || isDetachedMode()) {
      return 'standalone';
    }

    if (isEmbeddedIframe()) {
      return 'embedded';
    }

    fallbackToPopupLaunch();
    return 'fallback-popup';
  };
